import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getExerciseCardImageSrc } from "@/lib/exercise-media";
import { Dumbbell, Heart, Play, Star, Target } from "lucide-react";
import type { ReactNode } from "react";

export type ExerciseCardExercise = {
  id: string;
  name: string;
  categories?: string[];
  primary_muscles?: string[];
  secondary_muscles?: string[];
  equipment?: string[];
  difficulties?: string[];
  instructions?: string;
  is_compound?: boolean;
  video_url?: string | null;
  image_url?: string | null;
};

function getDifficultyColor(level: string): string {
  const colorMap: Record<string, string> = {
    Beginner: "bg-green-100 text-green-800",
    Intermediate: "bg-yellow-100 text-yellow-800",
    Advanced: "bg-blue-100 text-blue-800",
    Expert: "bg-orange-100 text-orange-800",
    Pro: "bg-red-100 text-red-800",
  };
  return colorMap[level] || "bg-gray-100 text-gray-800";
}

function getCategoryIcon(category?: string): ReactNode {
  switch (category) {
    case "push":
      return <Target className="w-4 h-4" />;
    case "pull":
      return <Dumbbell className="w-4 h-4" />;
    case "legs":
      return <Target className="w-4 h-4" />;
    case "core":
      return <Star className="w-4 h-4" />;
    case "cardio":
      return <Heart className="w-4 h-4" />;
    default:
      return <Target className="w-4 h-4" />;
  }
}

type Props = {
  exercise: ExerciseCardExercise;
  rightBadge?: ReactNode;
  topRight?: ReactNode;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
  variant?: "light" | "dark";
};

export default function ExerciseCard({ exercise, rightBadge, topRight, selected, onClick, className, variant = "light" }: Props) {
  const categories = exercise.categories ?? [];
  const primary = exercise.primary_muscles ?? [];
  const secondary = exercise.secondary_muscles ?? [];
  const equipment = exercise.equipment ?? [];
  const difficulties = exercise.difficulties ?? [];

  const clickable = Boolean(onClick);
  const isDark = variant === "dark";
  const imageSrc = getExerciseCardImageSrc({ name: exercise.name, image_url: exercise.image_url, video_url: exercise.video_url });

  return (
    <Card
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? Boolean(selected) : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (!clickable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={cn(
        "group relative rounded-xl overflow-hidden border shadow-card",
        isDark ? "bg-black border-primary/10" : "bg-white border-gray-200/60",
        clickable ? (isDark ? "cursor-pointer hover:ring-1 hover:ring-primary/20 transition-all" : "cursor-pointer hover:shadow-xl transition-all") : undefined,
        selected ? (isDark ? "ring-2 ring-primary/40 ring-offset-0" : "ring-2 ring-emerald-400 ring-offset-2") : undefined,
        isDark
          ? "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          : "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2",
        className
      )}
    >
      <div className="relative h-40 w-full">
        <img
          src={imageSrc}
          alt={`${exercise.name} demonstration`}
          className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

        {exercise.video_url ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className={cn(
              "h-12 w-12 rounded-full flex items-center justify-center",
              isDark ? "bg-white/15 ring-1 ring-white/20" : "bg-white/25 ring-1 ring-white/30"
            )}>
              <Play className={cn("h-6 w-6", isDark ? "text-white" : "text-white")} fill="currentColor" />
            </div>
          </div>
        ) : null}

        {(categories.length > 0 || exercise.is_compound) ? (
          <div className="absolute top-3 left-3 flex gap-2">
            {categories.length > 0 ? (
              <div className="flex gap-1">
                {categories.map((cat) => (
                  <Badge
                    key={cat}
                    variant="secondary"
                    className={cn(isDark ? "bg-black/40 text-white border border-white/10" : "bg-white/90 text-gray-800")}
                  >
                    {cat}
                  </Badge>
                ))}
              </div>
            ) : null}
            {exercise.is_compound ? (
              <Badge
                variant="outline"
                className={cn(isDark ? "bg-black/40 text-purple-200 border-purple-300/30" : "bg-white/90 text-purple-700 border-purple-200")}
              >
                Compound
              </Badge>
            ) : null}
          </div>
        ) : null}

        {topRight ? <div className="absolute top-3 right-3">{topRight}</div> : null}

        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
          <h3 className="!text-white text-lg font-semibold drop-shadow flex items-center gap-2">
            {getCategoryIcon(categories[0])}
            {exercise.name}
          </h3>
          {rightBadge ?? (
            <Badge variant="outline" className={cn(isDark ? "bg-black/40 text-white border-white/15 text-xs" : "bg-white/90 text-gray-800 text-xs")}>
              Library
            </Badge>
          )}
        </div>
      </div>

      <CardContent className="p-4">
        <div className="space-y-3">
          {primary.length > 0 ? (
            <div>
              <p className={cn("text-xs font-medium mb-1", isDark ? "text-gray-light" : "text-gray-700")}>Primary Muscles:</p>
              <div className="flex flex-wrap gap-1">
                {primary.map((muscle, index) => (
                  <Badge
                    key={`${muscle}-${index}`}
                    variant="secondary"
                    className={cn("text-xs", isDark ? "bg-dark-slate text-white border border-primary/10" : undefined)}
                  >
                    {muscle}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}

          {secondary.length > 0 ? (
            <div>
              <p className={cn("text-xs font-medium mb-1", isDark ? "text-gray-light" : "text-gray-700")}>Secondary Muscles:</p>
              <div className="flex flex-wrap gap-1">
                {secondary.map((muscle, index) => (
                  <Badge
                    key={`${muscle}-${index}`}
                    variant="outline"
                    className={cn("text-xs", isDark ? "text-white border-white/15" : undefined)}
                  >
                    {muscle}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}

          {equipment.length > 0 ? (
            <div>
              <p className={cn("text-xs font-medium mb-1", isDark ? "text-gray-light" : "text-gray-700")}>Equipment:</p>
              <div className="flex flex-wrap gap-1">
                {equipment.map((eq, index) => (
                  <Badge
                    key={`${eq}-${index}`}
                    variant="outline"
                    className={cn("text-xs", isDark ? "text-orange-200 border-orange-300/30" : "text-orange-600 border-orange-200")}
                  >
                    {eq}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}

          {difficulties.length > 0 ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 flex-wrap">
                {difficulties.map((level) => (
                  <div
                    key={level}
                    className={cn(
                      "px-2 py-0.5 rounded text-xs",
                      isDark ? "bg-white/10 text-white border border-white/10" : getDifficultyColor(String(level))
                    )}
                  >
                    {String(level)}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {exercise.instructions ? (
            <div>
              <p className={cn("text-xs font-medium mb-1", isDark ? "text-gray-light" : "text-gray-700")}>Instructions:</p>
              <p className={cn("text-xs line-clamp-3", isDark ? "text-gray-light" : "text-gray-600")}>{exercise.instructions}</p>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

